"use client";

import { useState, useRef, useEffect, startTransition } from "react";
import Image from "next/image";
import { usePathname, useRouter } from "@/i18n/navigation";
import { useLocale } from "next-intl";
import { createPortal } from "react-dom";

const languages = [
  { code: "en", label: "English" },
  { code: "vi", label: "Tiếng Việt" },
  { code: "id", label: "Bahasa Indonesia" },
  { code: "de", label: "Deutsch" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "it", label: "Italiano" },
  { code: "nl", label: "Nederlands" },
  { code: "pl", label: "Polski" },
  { code: "pt", label: "Português" },
  { code: "sv", label: "Svenska" },
  { code: "tr", label: "Türkçe" },
  { code: "el", label: "Ελληνικά" },
  { code: "ru", label: "Русский" },
  { code: "ar", label: "العربية" },
  { code: "hi", label: "हिन्दी" },
  { code: "th", label: "ไทย" },
  { code: "ko", label: "한국어" },
  { code: "ja", label: "日本語" },
  { code: "zh", label: "中文" },
];

export default function PickerLanguage() {
  const [open, setOpen] = useState(false);
  const [buttonRect, setButtonRect] = useState<DOMRect | null>(null);
  const [mounted, setMounted] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const router = useRouter();
  const pathname = usePathname();
  const locale = useLocale();

  useEffect(() => {
    setMounted(true);
  }, []);

  const handlePickLanguage = (lang: string) => {
    if (locale === lang) return;
    startTransition(() => {
      router.push(pathname, { locale: lang });
      router.refresh();
    });
    setOpen(false);
  };

  const handlePreloadLocale = (lang: string) => {
    if (locale === lang) return;
    if (typeof window !== "undefined") {
      import(`@/locales/${lang}.json`).catch(() => {});
    }
  };

  const handleToggle = () => {
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setButtonRect(rect);
    }
    setOpen(!open);
  };

  // Đóng dropdown khi click bên ngoài
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current &&
        buttonRef.current.contains(event.target as Node)
      ) {
        return;
      }

      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  // Đóng dropdown khi nhấn ESC
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const dropdownContent = open && buttonRect && (
    <div
      ref={containerRef}
      className="fixed left-0 right-0 z-[100] mt-2 md:left-1/2 md:right-auto md:w-[740px] md:-translate-x-1/2 lg:left-auto lg:w-[740px] lg:translate-x-0"
      style={{
        top: `${buttonRect.bottom + 8}px`,
        right:
          window.innerWidth >= 1024
            ? `${window.innerWidth - buttonRect.right}px`
            : undefined,
      }}
    >
      <div className=" grid grid-cols-2 rounded-[20px] border border-solid border-white bg-[rgba(243,248,255,0.60)] px-4 py-2 shadow-[0_4px_12px_0_rgba(0,30,75,0.12)] backdrop-blur-xl sm:grid-cols-3 md:mx-0 md:grid-cols-4">
        {languages.map((lang) => (
          <div
            key={lang.code}
            onClick={() => handlePickLanguage(lang.code)}
            onMouseEnter={() => handlePreloadLocale(lang.code)}
            className={`flex h-10 cursor-pointer items-center rounded-md px-3 hover:bg-black/5 ${
              locale === lang.code ? "bg-black/10 font-medium" : ""
            }`}
          >
            <p className="text-[13px] sm:text-sm font-light uppercase text-black">
              {lang.label}
            </p>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <>
      {/* Trigger Button */}
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="aspect-square h-6 w-6 cursor-pointer"
        aria-label="Select language"
      >
        <Image
          src="/images/icons/globe.svg"
          alt="globe"
          width={24}
          height={24}
          priority
        />
      </button>

      {mounted &&
        typeof window !== "undefined" &&
        dropdownContent &&
        createPortal(dropdownContent, document.body)}
    </>
  );
}
